import { useBoomWallet } from '@boomeme/wallet-sdk';
import { useAllSPLTokenAmount } from './useAllSPLTokenAmount';
import { useEffect, useRef } from 'react';
import { useAppStore } from '@/store';
import { IHoldingToken } from '@/request/token/token.response.interface';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { useTradeStore } from '@/page/trade/store';
import { useParams } from 'react-router-dom';
import BigNumber from 'bignumber.js';
import tokenApiService from '@/request/token/token.api.service';

export interface IHoldingTokenData extends IHoldingToken {
  amount: string;
  balance: string;
  decimals: number;
}

export const useCalcTotalBalance = () => {
  const { chainCoinBalance, chainCoinPrice, chainInfo, setTotalBalance, setChainCoinBalance } = useAppStore();
  const { userTokenList } = useAllSPLTokenAmount();
  const { walletAddress, chain } = useBoomWallet();
  const { address } = useParams();
  const { subscribe } = useAppEventBusContext();
  const { setChainTokenAmount } = useTradeStore();
  const hasFetchedBscData = useRef(false);

  const calculateTokenBalance = (tokenList: number[]) => {
    return tokenList.reduce((acc, item) => Number(acc) + Number(item), 0);
  };

  const getEffectiveTokens = (tokens: any[]) => {
    return tokens.filter((item) => Number(item.uiAmount) > 0.0001).map((item) => item.tokenAddress);
  };

  const calcSolBalance = async () => {
    if (!walletAddress || !userTokenList || chain !== 'solana') return;

    try {
      const chainBalance = BigNumber(chainCoinBalance).times(chainCoinPrice);

      const effectiveAddressList = getEffectiveTokens(userTokenList);
      if (effectiveAddressList.length === 0) {
        setTotalBalance(chainBalance.toString());
        return;
      }

      const res = await tokenApiService.getTokenPriceList(effectiveAddressList);

      if (res.code !== 0) return;

      const tokenBalances = userTokenList
        .map((item) => {
          const token = res.data.find((t) => t.address.toLocaleLowerCase() === item.tokenAddress.toLocaleLowerCase());
          if (!token) return 0;
          return Number(item.uiAmountString) * Number(token.price);
        })
        .filter(Boolean);

      const tokenBalance = calculateTokenBalance(tokenBalances);
      const totalBalance = tokenBalance + chainBalance.toString();
      setTotalBalance(totalBalance);
    } catch (error) {
      setTotalBalance('0');
    }
  };

  const tokenDecimap2ThresholdMap: { [key: number]: number } = {
    18: 0.0002,
    8: 0.00001,
    6: 0.001,
    4: 0.001,
    2: 0.01,
  };

  const calcBscBalance = async () => {
    if (!walletAddress || chainInfo.chain !== 'bsc') return;
    setChainCoinBalance('0');
    try {
      const res = await tokenApiService.getEvmWalletHoldingList({ wallet: walletAddress });
      if (res.code !== 0) return;

      if (res.data.length > 0) {
        hasFetchedBscData.current = true;
        processEvmData(res.data);
      }
    } catch (error) {
      console.error('Error calculating total balance:', error);
      setTotalBalance('0');
    }
  };

  const processEvmData = (data: any[]) => {
    let totalEvmBalance = BigNumber(0);
    if (data.length === 0) {
      setChainCoinBalance('0');
      setTotalBalance('0');
      return;
    }

    data.forEach((item) => {
      if (item.address.toLowerCase() === chainInfo.address.toLowerCase()) {
        setChainCoinBalance(item.balance);
      }

      if (address) {
        if (item.address.toLowerCase() === address?.toLowerCase()) {
          const threshold = tokenDecimap2ThresholdMap[item.decimals];
          if (+item.balance > threshold) {
            setChainTokenAmount(+item.balance - threshold);
          }
        }
      }

      const balance2usd = BigNumber(item.balance).times(item.price);
      totalEvmBalance = totalEvmBalance.plus(balance2usd);
    });

    setTotalBalance(totalEvmBalance.toString());
  };

  useEffect(() => {
    calcSolBalance();
  }, [walletAddress, userTokenList, chainCoinBalance, chainCoinPrice, chainInfo]);

  useEffect(() => {
    if (walletAddress && chainInfo.chain === 'bsc') {
      hasFetchedBscData.current = false;
      calcBscBalance();
    }
  }, [walletAddress, chainInfo, address]);

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Balance, () => {
      hasFetchedBscData.current = false;
      calcBscBalance();
    });
    return () => unsubscribe();
  }, [calcBscBalance]);
};
